import { Button, Checkbox, Grid } from "@mui/material";
import { planMapColumns } from "./map-columns";
import { useLoadData } from "../../../../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../../../../models/api-response";
import { planApiKeyStore } from "../../../../../../../api/plan-api-key-store";
import { SiTrackGrid } from "../../../../../../../components/si-track-grid/si-track-grid";
import { useEffect, useState } from "react";
import { useSaveData } from "../../../../../../../hooks/useSaveData";
import { UserPermissionName } from "../../../../../../../models/user-permission-name";
import { GridColumnConfig, GridPreference } from "../../../../../../../models/gridCoumnConfig";
import { gridApiKeyStore } from "../../../../../../../api/grid-api-key-store";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../../../../../hooks/use-debounce";
import { MutationKeyConfig } from "../../../../../../../api/api-helper";
import { InvalidateRoute } from "../../../../../../../hooks/useDeleteData";
import { useSnackbar } from "../../../../../../../hooks/use-snackbar.hook";
import { useAuthorize } from "../../../../../../../hooks/use-authorize";


interface CellProps {
    planData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface PlanRequestArgs {
    customerID?: number;
    projectID?: number;
    officeID?: number;
    unitID?: number;
}

interface MapPlanComponent {
    planData: any[];
    planListData: any;
    planRequestArgs: PlanRequestArgs;
    config: { saveConfig: MutationKeyConfig; invalidateRoute?: InvalidateRoute };
    onSavedPlan: () => void;
    onClosed: () => void;
}

export const PlanMappedComponent = ({
    planData,
    planListData,
    planRequestArgs,
    onSavedPlan,
    onClosed,
    config: { saveConfig, invalidateRoute }, }: MapPlanComponent) => {

    const { showSnackbar } = useSnackbar();
    const [selectedPlan, setSelectedPlan] = useState<any[]>([]);
    const [completeData, setCompleteData] = useState<any[]>([]);
    const [dataChange, setDataChange] = useState<boolean>(false);
    const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(
        planApiKeyStore.getPlanMapList({ projectID: planRequestArgs?.projectID, unitID: planRequestArgs?.unitID, mapLevel: 'unit' })
    );

    const { isLoading: isMapPlanLoading, mutateAsync } = useSaveData<any>(saveConfig, invalidateRoute);

    useEffect(() => {
        if (data) {
            const updatedData = data?.data.map((d) => {
                const matchingPlan = planData?.find((s) => s.planID === d.planID);
                return {
                    ...d,
                    isSelected: !!matchingPlan,
                    isPlanReview: matchingPlan?.planReviewTypeID === 1,
                    isRebateProcess: matchingPlan?.planReviewTypeID === 2,
                };
            });
            const uniquePlans = updatedData?.filter(
                (item, index, self) => index === self.findIndex((t) => t.planID === item.planID)
            );
            setSelectedPlan(uniquePlans?.filter((p: any) => p.isSelected === true));
            setCompleteData(uniquePlans);
        }
    }, [data]);

    const validatePlans = (data1: any, data2: any) => {
        const relevantTypes = [1, 3];

        const data1Types = new Set(
            data1
                .filter((item: any) => relevantTypes.includes(item.planFileTypeID))
                .map((item: any) => item.planFileTypeID)
        );

        return data2.some((item: any) => data1Types.has(item.planFileTypeID));
    };


    const hasMultiplePlanFileTypes = (data: any) => {
        const masterPlan = data.filter((item: any) => item.planFileTypeID === 1);
        if (masterPlan?.length > 1) return true;
        const energyModelingPlan = data.filter((item: any) => item.planFileTypeID === 3);
        if (energyModelingPlan?.length > 1) return true;
        return false;
    };

    const handleSelectedItem = (values: any[]) => {

        // const isValid = validatePlans(planListData, values);
        // if (isValid) {
        //     setDataChange(!dataChange);
        //     showSnackbar('Only one masterplan/ energy modelling file can be added to a JobSite.', 'error');
        //     return;
        // }

        const isMultipleData = hasMultiplePlanFileTypes(values);
        if (isMultipleData) {
            // setSelectedPlan([]);
            setDataChange(!dataChange);
            showSnackbar('A plan cannot be mapped to more than one master plan or energy modeling file.', 'error');
            return;
        }

        const updatedData = completeData?.map((d) => {
            const matchingPlan = values.some((s) => s.planID === d.planID);
            return {
                ...d,
                isSelected: matchingPlan,
                ...(matchingPlan === false && { isPlanReview: false, isRebateProcess: false }),
            };
        });
        setCompleteData(updatedData)
        setSelectedPlan(values);
    };

    const handleChangePlanReview = (planID: any) => {
        setSelectedPlan((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isPlanReview: !plan.isPlanReview, isRebateProcess: false } : plan
            )
        );
        setCompleteData((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isPlanReview: !plan.isPlanReview, isRebateProcess: false } : plan
            )
        );
    };

    const handleChangeRebateProcess = (planID: any) => {
        setSelectedPlan((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isRebateProcess: !plan.isRebateProcess, isPlanReview: false, } : plan
            )
        );
        setCompleteData((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isRebateProcess: !plan.isRebateProcess, isPlanReview: false, } : plan
            )
        );
    };

    const PlanReview = ({ planData }: CellProps) => {
        const onCheckboxChange = () => {
            handleChangePlanReview(planData?.planID)
        }
        return (
            <td>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox checked={planData?.isPlanReview} onChange={onCheckboxChange} />
                </div>
            </td>
        );
    };

    const RebateProcess = ({ planData }: CellProps) => {
        const onCheckboxChange = () => {
            handleChangeRebateProcess(planData?.planID)
        }
        return (
            <td>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox checked={planData?.isRebateProcess} onChange={onCheckboxChange} />
                </div>
            </td>
        );
    };


    const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
        if (field === 'planReview') {
            return (dataItem: any) => {
                return <PlanReview planData={dataItem.dataItem} />;
            };
        }

        if (field === 'rebateProcess') {
            return (dataItem: any) => {
                return <RebateProcess planData={dataItem.dataItem} />;
            };
        }
        // Return undefined if the field is not 'bluePrint'
        return undefined;
    };

    const submitData = async () => {

        const transformedPlans = selectedPlan.map((plan: any) => ({
            planID: plan.planID,
            planReviewTypeID: plan.isPlanReview ? 1 : plan.isRebateProcess ? 2 : 0,
        }));

        const uniquePlans = transformedPlans?.reduce((acc: any, current: any) => {
            if (!acc.some((item: any) => item.planID === current.planID)) {
                acc.push(current);
            }
            return acc;
        }, []);

        const mappedData = {
            unitIDs: [planRequestArgs?.unitID],
            plans: uniquePlans
        }

        await mutateAsync({ data: mappedData });
        onSavedPlan();
    }

    // -------------------------------------------------Grid Layout------------------------------

    const { isLoading: isLoadingGridLayout, data: gridLayouts } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout('PlanReviewExistingPlan', 'PlanReviewExistingPlan')
    );

    const { mutateAsync: mutateExistingPlanAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredPlanReviewExistingPlans, setFilteredPlanReviewExistingPlans] = useState<any[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredPlanReviewExistingPlanList = completeData?.filter((PlanReviewExistingPlan) =>
            Object.values(PlanReviewExistingPlan).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredPlanReviewExistingPlans(filteredPlanReviewExistingPlanList || []);
    }, [debouncedSearchTerm, completeData, dataChange]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: 'PlanReviewExistingPlan',
            pageName: 'PlanReviewExistingPlan',
        };

        mutateExistingPlanAsync({ data });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            if (filterData && columnJson) {
                setGridConfig({
                    columns: columnJson,
                    dataState: filterData,
                });
            }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Grid container spacing={2}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
                <SiTrackGrid
                    fileTitle='mapped_plan'
                    selectedField="isSelected"
                    dataItemKey="planID"
                    columns={planMapColumns}
                    customCell={(field) =>
                        CustomGridCell({
                            field,
                        })
                    }
                    onSelectedItems={handleSelectedItem}
                    actionPermissions={{
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    isBusy={isLoading || isMapPlanLoading}

                    // -----------------Grid Layout-------
                    dataItems={filteredPlanReviewExistingPlans || []}
                    reorderable
                    handleSearch={handleInputChange}
                    handleSaveLayout={handleSaveLayout}
                    isSaveLayout={true}
                    gridConfigFromApi={gridConfig}
                // -----------------Grid Layout-------
                />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12} className="spaceBetween">
                <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                    Cancel
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    style={{ width: '130px' }}
                    onClick={submitData}
                    disabled={isReadOnlyPermission}
                >
                    Save
                </Button>
            </Grid>
        </Grid>
    );
}